import swal from 'sweetalert';
import DetectRTC from 'detectrtc';

// A docked browser panel (Copilot/Edge sidebar, Chrome side panel, devtools) steals at least
// this many pixels from the viewport. Well above scrollbars and browser find bars.
export const SIDE_PANEL_MIN_SIZE = 120;

// Width the browser window is not giving to the page. Window borders and a scrollbar account
// for a few pixels; anything more means a panel is docked beside the page (or the page is
// zoomed out). Vertical only - horizontal toolbars would pollute a height comparison.
export const getViewportGap = () => Math.max(0, window.outerWidth - window.innerWidth);

// Shared by the exam security system and the pre-exam system check
export const isViewportObstructed = () => getViewportGap() >= SIDE_PANEL_MIN_SIZE;

// Smallest on-screen panel worth reporting. Extension sidebars are hundreds of pixels wide;
// this is far above the tracking pixels and hidden helper frames legitimate scripts leave behind.
const OVERLAY_MIN_SIZE = 150;
// Everything this app renders outside #root: sweetalert dialogs and Material-UI portals
const APP_OVERLAY_SELECTOR = '[class^="swal"],[class*=" swal"],[class^="Mui"],[class*=" Mui"]';

// Google Identity Services (loaded in index.html) injects its own frames and containers
const isGoogleNode = (el) => {
  const src = el.getAttribute && (el.getAttribute('src') || '');
  if (src && (src.includes('accounts.google.com') || src.includes('gstatic.com'))) return true;
  const id = el.id || '';
  return id.startsWith('credential_picker') || id.startsWith('g_id') || id.startsWith('g_a11y');
};

// Detect panels drawn INSIDE the page - browser extensions that inject an AI assistant as an
// overlay steal no viewport width, fire no resize and never move focus out of the document,
// so geometry and focus checks are both blind to them. What they cannot hide is the node they
// have to add to the DOM to be visible.
export const findPageOverlays = () => {
  const root = document.getElementById('root');
  const overlays = [];

  const isOurs = (el) => {
    if (root && (el === root || root.contains(el))) return true;
    if (el.matches && el.matches(APP_OVERLAY_SELECTOR)) return true;
    if (el.querySelector && el.querySelector(APP_OVERLAY_SELECTOR)) return true;
    return isGoogleNode(el);
  };

  // How much of the element is actually on screen. Panels are often "closed" by sliding them
  // out of the viewport rather than removing them, and those must not count as covering the page.
  const visibleSize = (el) => {
    const rect = el.getBoundingClientRect();
    return {
      width: Math.min(rect.right, window.innerWidth) - Math.max(rect.left, 0),
      height: Math.min(rect.bottom, window.innerHeight) - Math.max(rect.top, 0)
    };
  };

  const isVisiblePanel = (el) => {
    const style = window.getComputedStyle(el);
    if (style.display === 'none' || style.visibility === 'hidden') return false;
    if (parseFloat(style.opacity || '1') < 0.1) return false;
    const { width, height } = visibleSize(el);
    return width >= OVERLAY_MIN_SIZE && height >= OVERLAY_MIN_SIZE;
  };

  const describe = (el) => {
    const { width, height } = visibleSize(el);
    return `${el.tagName.toLowerCase()}${el.id ? '#' + el.id : ''} ${Math.round(width)}x${Math.round(height)}`;
  };

  // Anything sizeable attached straight to <body> that this app did not put there
  const reported = [];
  const ignoredTags = ['SCRIPT', 'STYLE', 'LINK', 'NOSCRIPT', 'TEMPLATE'];
  Array.from(document.body.children).forEach(el => {
    if (ignoredTags.includes(el.tagName)) return;
    if (isOurs(el)) return;
    if (!isVisiblePanel(el)) return;
    reported.push(el);
    overlays.push(describe(el));
  });

  // Extension frames injected anywhere in the page. Every iframe this app renders is inside
  // #root and has a real src, so a sizeable srcless or extension-scheme frame is not ours.
  Array.from(document.querySelectorAll('iframe')).forEach(frame => {
    if (root && root.contains(frame)) return;
    // Already counted as part of the wrapper it lives in
    if (reported.some(el => el.contains(frame))) return;
    const src = frame.getAttribute('src') || '';
    const isExtensionFrame = !src || src.startsWith('chrome-extension://') || src.startsWith('moz-extension://');
    if (!isExtensionFrame) return;
    if (isGoogleNode(frame)) return;
    if (!isVisiblePanel(frame)) return;
    reported.push(frame);
    overlays.push(describe(frame));
  });

  return overlays;
};
// A panel left open for the whole assessment is re-reported this often, so ignoring the
// warning keeps costing violations instead of buying a permanently obstructed screen
const SIDE_PANEL_REMINDER_MS = 15000;
// Ignore focus/viewport noise while the exam page is still settling (webcam prompt, layout)
const SECURITY_WARMUP_MS = 5000;

class ExamSecurity {
  constructor() {
    // Check for cheat detection disable mode - only disables security features
    this.isCheatDetectionDisabled = (typeof process !== 'undefined' && process && process.env && process.env.REACT_APP_DISABLE_CHEAT_DETECTION === '1');
    
    this.cheatCounts = {
      copyPaste: 0,
      tabSwitch: 0,
      fullscreen: 0,
      altKey: 0,
      ctrlKey: 0,
      webcamOff: 0,
      contextMenu: 0,
      noPerson: 0,
      multiplePersons: 0,
      suspiciousObjects: 0,
      sidePanel: 0,
      focusLoss: 0
    };
    this.isWebcamRequired = true;
    this.isFullscreenRequired = true;
    this.timer = null;
    this.webcamCheckInterval = null;
    this.viewportBaseline = null; // Clean viewport size, used to spot docked panels
    this.sidePanelReported = false; // One violation per panel opening, not per check
    this.sidePanelReportedAt = 0;
    this.viewportCheckInterval = null;
    this.viewportCheckTimeout = null;
    this.documentHadFocus = true; // Report focus loss on transition only
    this.focusCheckInterval = null;
    this.focusCheckTimeout = null;
    this.securityStartedAt = 0;
    this.isExamActive = false;
    this.isSubmissionInProgress = false; // Track submission state
    this.onCheatDetected = null;
    this.onFullscreenExit = null;
    this.lastViolationTime = {}; // Track last violation time for each type
    this.violationCooldown = 5000; // 5 seconds cooldown between same type violations
    this.fileUploadActive = false; // Track if file upload is active
    this.fileUploadTimeout = null; // Timeout for file upload detection
    this.currentQuestionType = null; // Track current question type
    this.videoPreviewFullscreenActive = false; // Track if video preview fullscreen is active
    this.cheatDetectionSettings = {}; // Store individual cheat detection settings
  }

  // Check if a specific cheat detection feature is enabled
  isFeatureEnabled(feature) {
    if (!this.cheatDetectionSettings || typeof this.cheatDetectionSettings[feature] !== 'boolean') {
      // Default to enabled if not specified
      return true;
    }
    return this.cheatDetectionSettings[feature];
  }

  // Initialize security features
  init(options = {}) {
    // If cheat detection is disabled, skip all security initialization
    if (this.isCheatDetectionDisabled) {
      console.log('🔧 CHEAT DETECTION DISABLED: All security features disabled for testing');
      return;
    }
    
    this.isWebcamRequired = options.webcamRequired !== false;
    this.isFullscreenRequired = options.fullscreenRequired !== false;
    this.onCheatDetected = options.onCheatDetected;
    this.onFullscreenExit = options.onFullscreenExit;
    this.isExamActive = true;
    this.securityStartedAt = Date.now();

    // Store individual cheat detection settings
    this.cheatDetectionSettings = options.cheatDetectionSettings || {};

    this.loadCheatCounts();
    
    // Only initialize features that are enabled
    if (this.isFeatureEnabled('enableCopyPasteDetection')) {
      this.preventCopyPaste(); // Re-enabled with video question support
    }
    if (this.isFeatureEnabled('enableTabSwitchDetection')) {
      this.detectTabSwitch();
    }
    if (this.isFeatureEnabled('enableFullscreenDetection')) {
      this.detectFullscreenExit();
    }
    if (this.isFeatureEnabled('enableKeyboardDetection')) {
      this.detectKeyCombinations(); // Re-enabled after testing
    }
    if (this.isFeatureEnabled('enableContextMenuDetection')) {
      this.preventContextMenu();
    }
    if (this.isFeatureEnabled('enableWebcamDetection')) {
      this.enforceWebcam();
      this.startWebcamMonitoring();
    }
    if (this.isFeatureEnabled('enableSidePanelDetection')) {
      this.detectSidePanel();
    }
    if (this.isFeatureEnabled('enableFocusLossDetection')) {
      this.detectFocusLoss();
    }
  }

  // Load cheat counts from session storage
  loadCheatCounts() {
    // If cheat detection is disabled, set all counts to 0
    if (this.isCheatDetectionDisabled) {
      this.cheatCounts = {
        copyPaste: 0,
        tabSwitch: 0,
        fullscreen: 0,
        altKey: 0,
        ctrlKey: 0,
        webcamOff: 0,
        contextMenu: 0,
        noPerson: 0,
        multiplePersons: 0,
        suspiciousObjects: 0,
        sidePanel: 0,
        focusLoss: 0
      };
      return;
    }
    
    this.cheatCounts = {
      copyPaste: parseInt(sessionStorage.getItem("count_copypaste") || "0"),
      tabSwitch: parseInt(sessionStorage.getItem("count_tabchange") || "0"),
      fullscreen: parseInt(sessionStorage.getItem("count_fullscreen") || "0"),
      altKey: parseInt(sessionStorage.getItem("countalt") || "0"),
      ctrlKey: parseInt(sessionStorage.getItem("countctrl") || "0"),
      webcamOff: parseInt(sessionStorage.getItem("count_webcamoff") || "0"),
      contextMenu: parseInt(sessionStorage.getItem("count_contextmenu") || "0"),
      noPerson: parseInt(sessionStorage.getItem("count_noPerson") || "0"),
      multiplePersons: parseInt(sessionStorage.getItem("count_multiplePersons") || "0"),
      suspiciousObjects: parseInt(sessionStorage.getItem("count_suspiciousObjects") || "0"),
      sidePanel: parseInt(sessionStorage.getItem("count_sidepanel") || "0"),
      focusLoss: parseInt(sessionStorage.getItem("count_focusloss") || "0")
    };
  }

  // Save cheat counts to session storage
  saveCheatCounts() {
    // If cheat detection is disabled, don't save any counts
    if (this.isCheatDetectionDisabled) {
      return;
    }
    
    // Map internal keys to session storage keys
    const keyMapping = {
      copyPaste: 'count_copypaste',
      tabSwitch: 'count_tabchange',
      fullscreen: 'count_fullscreen',
      altKey: 'countalt',
      ctrlKey: 'countctrl',
      webcamOff: 'count_webcamoff',
      contextMenu: 'count_contextmenu',
      noPerson: 'count_noPerson',
      multiplePersons: 'count_multiplePersons',
      suspiciousObjects: 'count_suspiciousObjects',
      sidePanel: 'count_sidepanel',
      focusLoss: 'count_focusloss'
    };

    Object.keys(this.cheatCounts).forEach(key => {
      const sessionKey = keyMapping[key] || `count_${key}`;
      sessionStorage.setItem(sessionKey, this.cheatCounts[key].toString());
    });
  }

  // Update current question type (called from exam screen)
  updateCurrentQuestionType(questionType) {
    this.currentQuestionType = questionType;
  }

  // Pause security alerts during submission
  pauseSecurityAlerts() {
    this.isSubmissionInProgress = true;
  }

  // Resume security alerts after submission
  resumeSecurityAlerts() {
    this.isSubmissionInProgress = false;
  }

  // Prevent copy-paste functionality
  preventCopyPaste() {
    // If cheat detection is disabled, skip copy-paste prevention
    if (this.isCheatDetectionDisabled) {
      return;
    }
    
    // If copy-paste detection is disabled, skip copy-paste prevention
    if (!this.isFeatureEnabled('enableCopyPasteDetection')) {
      console.log('🔧 Copy-paste detection disabled - allowing copy-paste');
      return;
    }
    
    const preventCopyPasteHandler = (e) => {
      if (!this.isExamActive || this.isSubmissionInProgress) return;
      
      // Allow copy-paste for video questions
      if (this.currentQuestionType === 'video') {
        return;
      }
      
      // Prevent copy-paste for other question types
      if (e.ctrlKey || e.metaKey) {
        if (e.key === 'c' || e.key === 'v' || e.key === 'x' || e.key === 'a') {
          e.preventDefault();
          this.recordCheat('copyPaste', 'Copy-paste attempt detected');
          return false;
        }
      }
    };

    document.addEventListener('keydown', preventCopyPasteHandler);
    this.handlers = { ...this.handlers, copyPaste: preventCopyPasteHandler };
  }

  // Prevent context menu
  preventContextMenu() {
    // If cheat detection is disabled, skip context menu prevention
    if (this.isCheatDetectionDisabled) {
      return;
    }
    
    // If context menu detection is disabled, skip context menu prevention
    if (!this.isFeatureEnabled('enableContextMenuDetection')) {
      console.log('🔧 Context menu detection disabled - allowing context menu');
      return;
    }
    
    const preventContextMenuHandler = (e) => {
      if (!this.isExamActive || this.isSubmissionInProgress) return;
      e.preventDefault();
      this.recordCheat('contextMenu', 'Context menu attempt detected');
      return false;
    };

    const preventDragDropHandler = (e) => {
      if (!this.isExamActive || this.isSubmissionInProgress) return;
      e.preventDefault();
      this.recordCheat('copyPaste', 'Drag-drop attempt detected');
      return false;
    };

    document.addEventListener('contextmenu', preventContextMenuHandler);
    document.addEventListener('dragstart', preventDragDropHandler);
    document.addEventListener('drop', preventDragDropHandler);
    this.handlers = { 
      ...this.handlers, 
      contextMenu: preventContextMenuHandler,
      dragDrop: preventDragDropHandler
    };
  }

  // Detect tab switching
  detectTabSwitch() {
    // If cheat detection is disabled, skip tab switch detection
    if (this.isCheatDetectionDisabled) {
      return;
    }
    
    // If tab switch detection is disabled, skip tab switch detection
    if (!this.isFeatureEnabled('enableTabSwitchDetection')) {
      console.log('🔧 Tab switch detection disabled - allowing tab switching');
      return;
    }
    
    const handleVisibilityChange = () => {
      if (!this.isExamActive || this.isSubmissionInProgress) return;
      
      if (document.hidden) {
        this.recordCheat('tabSwitch', 'Tab switching detected');
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    this.handlers = { ...this.handlers, visibilitychange: handleVisibilityChange };
  }

  // Enable file upload mode (temporarily disable fullscreen checks)
  enableFileUpload() {
    this.fileUploadActive = true;
    if (this.fileUploadTimeout) {
      clearTimeout(this.fileUploadTimeout);
    }
    this.fileUploadTimeout = setTimeout(() => {
      this.fileUploadActive = false;
    }, 3000); // 3 seconds timeout
  }

  // Enable video preview fullscreen mode (temporarily disable fullscreen detection)
  enableVideoPreviewFullscreen() {
    this.videoPreviewFullscreenActive = true;
    console.log('Video preview fullscreen enabled - fullscreen exit detection disabled');
  }

  // Disable video preview fullscreen mode (re-enable fullscreen detection)
  disableVideoPreviewFullscreen() {
    this.videoPreviewFullscreenActive = false;
    console.log('Video preview fullscreen disabled - fullscreen exit detection re-enabled');
  }

  // Detect fullscreen exit
  detectFullscreenExit() {
    // If cheat detection is disabled, skip fullscreen detection
    if (this.isCheatDetectionDisabled) {
      return;
    }
    
    const handleFullscreenChange = () => {
      if (!this.isExamActive || this.isSubmissionInProgress) return;
      
      // If file upload is active, delay the fullscreen check
      if (this.fileUploadActive) {
        return; // Skip fullscreen check during file upload
      }
      
      // If video preview fullscreen is active, skip the fullscreen check
      if (this.videoPreviewFullscreenActive) {
        return; // Skip fullscreen check during video preview fullscreen
      }
      
      if (!document.fullscreenElement && this.isFullscreenRequired) {
        this.recordCheat('fullscreen', 'Fullscreen exit detected');
        if (this.onFullscreenExit) {
          this.onFullscreenExit();
        }
      }
    };

    // Monitor for file input focus/blur to detect file upload dialogs
    const handleFileInputFocus = () => {
      this.enableFileUpload();
    };

    const handleFileInputBlur = () => {
      // Keep file upload active for a bit longer to ensure dialog is fully closed
      setTimeout(() => {
        this.fileUploadActive = false;
      }, 1000);
    };

    // Add event listeners for file inputs
    const handleFileInputAdded = () => {
      const fileInputs = document.querySelectorAll('input[type="file"]');
      fileInputs.forEach(input => {
        input.addEventListener('focus', handleFileInputFocus);
        input.addEventListener('blur', handleFileInputBlur);
        input.addEventListener('change', handleFileInputBlur);
        input.addEventListener('click', handleFileInputFocus);
      });
    };

    // Initial setup
    handleFileInputAdded();

    // Monitor for dynamically added file inputs
    const observer = new MutationObserver(() => {
      handleFileInputAdded();
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    
    // Store handlers for cleanup
    this.handlers = { 
      ...this.handlers, 
      fullscreenchange: handleFullscreenChange,
      fileInputFocus: handleFileInputFocus,
      fileInputBlur: handleFileInputBlur,
      fileInputAdded: handleFileInputAdded,
      observer: observer
    };
  }

  // Remember the viewport size with nothing docked next to the page
  captureViewportBaseline() {
    this.viewportBaseline = {
      width: window.innerWidth,
      height: window.innerHeight,
      chromeWidth: window.outerWidth - window.innerWidth
    };
  }

  // Detect browser side panels and split views - Copilot/Edge sidebar, Chrome side panel,
  // docked devtools, snapped windows. These keep the tab visible AND fullscreen, so neither
  // visibilitychange nor fullscreenchange fires; the only trace is the viewport losing space
  // to the docked panel.
  detectSidePanel() {
    if (this.isCheatDetectionDisabled) {
      return;
    }

    if (!this.isFeatureEnabled('enableSidePanelDetection')) {
      console.log('🔧 Side panel detection disabled');
      return;
    }

    this.captureViewportBaseline();

    const checkViewport = () => {
      if (!this.isExamActive || this.isSubmissionInProgress) return;
      if (this.fileUploadActive || this.videoPreviewFullscreenActive) return;
      if (Date.now() - this.securityStartedAt < SECURITY_WARMUP_MS) return;
      if (!this.viewportBaseline) return;

      // Extension panels drawn inside the page steal no viewport space at all, so geometry
      // cannot see them - look for the node they had to inject to be visible. This works
      // whether or not the page is fullscreen, so it runs before the geometry checks.
      const injectedOverlays = findPageOverlays();

      // Window measurements only mean something while the page is fullscreen. Outside it a
      // smaller viewport just means the window is not maximised, and a viewport that shrank
      // because the candidate left fullscreen is the fullscreen detector's business.
      const geometryMeaningful = !this.isFullscreenRequired || !!document.fullscreenElement;

      let obstructedAtRest = false;
      let widthLoss = 0;
      let heightLoss = 0;
      let chromeGrowth = 0;
      let windowGap = 0;

      if (geometryMeaningful) {
        // Absolute check: in fullscreen the page fills the window, so outer and inner width
        // should match. A permanent gap means something is docked beside the page (or the
        // page is zoomed out). This catches a panel that was ALREADY open when the assessment
        // started - the baseline would otherwise have accepted it as normal.
        windowGap = window.outerWidth - window.innerWidth;
        obstructedAtRest = !!document.fullscreenElement && windowGap >= SIDE_PANEL_MIN_SIZE;
        widthLoss = this.viewportBaseline.width - window.innerWidth;
        heightLoss = this.viewportBaseline.height - window.innerHeight;
        chromeGrowth = windowGap - this.viewportBaseline.chromeWidth;

        // Viewport is back to (or larger than) its clean size - that is the new baseline
        if (!obstructedAtRest && injectedOverlays.length === 0 && widthLoss <= 0 && heightLoss <= 0) {
          this.captureViewportBaseline();
          this.sidePanelReported = false;
          return;
        }
      }

      const panelOpen = obstructedAtRest
        || injectedOverlays.length > 0
        || widthLoss >= SIDE_PANEL_MIN_SIZE
        || heightLoss >= SIDE_PANEL_MIN_SIZE
        || chromeGrowth >= SIDE_PANEL_MIN_SIZE;

      if (!panelOpen) {
        this.sidePanelReported = false;
        return;
      }

      // Already flagged. Stay quiet unless it is still there after the reminder interval -
      // a panel left open for the whole assessment should keep costing warnings.
      if (this.sidePanelReported && Date.now() - this.sidePanelReportedAt < SIDE_PANEL_REMINDER_MS) {
        return;
      }
      this.sidePanelReported = true;
      this.sidePanelReportedAt = Date.now();

      const lostPixels = Math.max(widthLoss, heightLoss, windowGap);
      console.log(`🔍 Side panel detected: ${lostPixels}px unaccounted for (inner ${window.innerWidth}x${window.innerHeight}, outer ${window.outerWidth}, gap ${windowGap}, alreadyOpenAtStart: ${obstructedAtRest && widthLoss <= 0}), injected overlays: ${injectedOverlays.join(', ') || 'none'}`);
      this.recordCheat('sidePanel', 'Browser side panel or split screen detected');
    };

    const handleResize = () => {
      // Let the layout settle before measuring
      clearTimeout(this.viewportCheckTimeout);
      this.viewportCheckTimeout = setTimeout(checkViewport, 400);
    };

    window.addEventListener('resize', handleResize);
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', handleResize);
    }
    // Poll as well - a panel can be docked without a resize event reaching us
    this.viewportCheckInterval = setInterval(checkViewport, 3000);

    this.handlers = { ...this.handlers, viewportResize: handleResize };
  }

  // Detect the assessment losing focus while it is still visible on screen. Clicking into a
  // side panel, a second monitor or another window never fires visibilitychange - the page
  // stays visible, it just stops being the focused document.
  detectFocusLoss() {
    if (this.isCheatDetectionDisabled) {
      return;
    }

    if (!this.isFeatureEnabled('enableFocusLossDetection')) {
      console.log('🔧 Focus loss detection disabled');
      return;
    }

    this.documentHadFocus = document.hasFocus();

    const checkFocus = () => {
      if (!this.isExamActive || this.isSubmissionInProgress) return;
      if (this.fileUploadActive || this.videoPreviewFullscreenActive) return;
      if (Date.now() - this.securityStartedAt < SECURITY_WARMUP_MS) return;

      const hasFocus = document.hasFocus();

      // Report the moment focus leaves, not for every second it stays away.
      // document.hidden means the tab was switched - that is the tab switch detector's job.
      if (this.documentHadFocus && !hasFocus && !document.hidden) {
        console.log('🔍 Focus left the assessment window while it was still visible');
        this.recordCheat('focusLoss', 'Assessment window lost focus');
      }

      this.documentHadFocus = hasFocus;
    };

    const handleBlur = () => {
      // Give transient blurs (permission prompts, tab switches) a moment to resolve first
      clearTimeout(this.focusCheckTimeout);
      this.focusCheckTimeout = setTimeout(checkFocus, 500);
    };

    const handleFocus = () => {
      this.documentHadFocus = true;
    };

    window.addEventListener('blur', handleBlur);
    window.addEventListener('focus', handleFocus);
    // Poll as well, in case a blur event never reaches us
    this.focusCheckInterval = setInterval(checkFocus, 2000);

    this.handlers = { ...this.handlers, windowBlur: handleBlur, windowFocus: handleFocus };
  }

  // Detect key combinations
  detectKeyCombinations() {
    // If cheat detection is disabled, skip key combination detection
    if (this.isCheatDetectionDisabled) {
      return;
    }
    
    // If keyboard detection is disabled, skip key combination detection
    if (!this.isFeatureEnabled('enableKeyboardDetection')) {
      console.log('🔧 Keyboard detection disabled - allowing keyboard shortcuts');
      return;
    }
    
    const handleKeydown = (e) => {
      if (!this.isExamActive || this.isSubmissionInProgress) return;
      
      // Allow certain keys for video questions
      if (this.currentQuestionType === 'video') {
        // Allow space, arrow keys, and media controls for video
        if (e.key === ' ' || e.key === 'Space' || 
            e.key === 'ArrowLeft' || e.key === 'ArrowRight' || 
            e.key === 'ArrowUp' || e.key === 'ArrowDown' ||
            e.key === 'MediaPlayPause' || e.key === 'MediaTrackNext' || 
            e.key === 'MediaTrackPrevious') {
          return;
        }
      }

      // Alt key
      if (e.key === 'Alt' || e.altKey) {
        e.preventDefault();
        this.recordCheat('altKey', 'Alt key pressed');
        return false;
      }
      
      // Ctrl key (but allow for video questions)
      if (e.key === 'Control' || e.ctrlKey) {
        e.preventDefault();
        this.recordCheat('ctrlKey', 'Control key pressed');
        return false;
      }
      
      // Command key (Mac) or Windows key (but allow for video questions)
      if (e.metaKey || e.key === 'Meta') {
        e.preventDefault();
        this.recordCheat('ctrlKey', 'Command/Windows key pressed');
        return false;
      }
      
      // Function keys (F1-F12)
      if (e.key.startsWith('F') && e.key.length <= 3) {
        e.preventDefault();
        this.recordCheat('ctrlKey', 'Function key pressed');
        return false;
      }
      
      // Print Screen
      if (e.key === 'PrintScreen' || e.keyCode === 44) {
        e.preventDefault();
        this.recordCheat('copyPaste', 'Print Screen detected');
        return false;
      }
    };

    document.addEventListener('keydown', handleKeydown);
    this.handlers = { ...this.handlers, keydown: handleKeydown };
  }

  // Enforce webcam requirement
  enforceWebcam() {
    // If cheat detection is disabled, skip webcam enforcement
    if (this.isCheatDetectionDisabled) {
      return;
    }
    
    if (!this.isWebcamRequired) return;

    DetectRTC.load(() => {
      const webcam = DetectRTC.isWebsiteHasWebcamPermissions;
      if (!webcam) {
        this.recordCheat('webcamOff', 'Webcam access denied or blocked');
      }
    });
  }

  // Start webcam monitoring
  startWebcamMonitoring() {
    // If cheat detection is disabled, skip webcam monitoring
    if (this.isCheatDetectionDisabled) {
      return;
    }
    
    if (!this.isWebcamRequired) return;

    // Reduce monitoring frequency from every 1 second to every 3 seconds
    this.webcamCheckInterval = setInterval(() => {
      if (!this.isExamActive) return;

      DetectRTC.load(() => {
        const webcam = DetectRTC.isWebsiteHasWebcamPermissions;
        if (!webcam) {
          this.recordCheat('webcamOff', 'Webcam access denied or blocked');
        }
      });
    }, 3000); // Increased from 1000ms to 3000ms for better performance
  }

  // Record a cheat attempt
  // Returns true when the violation was actually recorded (and onCheatDetected fired),
  // false when it was ignored (disabled feature, inactive exam or cooldown)
  recordCheat(type, message) {
    // If cheat detection is disabled, don't record any cheats
    if (this.isCheatDetectionDisabled) {
      console.log(`🔧 CHEAT DETECTION DISABLED: Cheat attempt ignored - ${type}: ${message}`);
      return false;
    }

    if (!this.isExamActive || this.isSubmissionInProgress) return false;

    const currentTime = Date.now();
    const lastTime = this.lastViolationTime[type] || 0;

    // Check if enough time has passed since last violation of this type
    if (currentTime - lastTime < this.violationCooldown) {
      return false; // Skip if within cooldown period
    }
    
    // Update last violation time
    this.lastViolationTime[type] = currentTime;
    
    this.cheatCounts[type]++;
    this.saveCheatCounts();

    // Professional alert messages
    const professionalMessages = {
      copyPaste: "⚠️ Copy-paste detected. Please type your responses manually.",
      tabSwitch: "⚠️ Tab switching detected. Please remain on the exam page.",
      fullscreen: "⚠️ Fullscreen exit detected. Please return to fullscreen mode.",
      altKey: "⚠️ Alt/Option key detected. Please avoid function keys.",
      ctrlKey: "⚠️ Control/Command key detected. Please avoid keyboard shortcuts.",
      webcamOff: "⚠️ Camera access issue. Please check your webcam connection.",
      contextMenu: "⚠️ Right-click detected. Context menu is disabled during exams.",
      noPerson: "⚠️ No person detected. Please ensure you are visible in camera.",
      multiplePersons: "⚠️ Multiple persons detected. Please ensure only you are visible.",
      suspiciousObjects: "⚠️ Suspicious object detected. Please clear your workspace.",
      sidePanel: "⚠️ Browser side panel or split screen detected. Please close it and keep only the assessment on screen.",
      focusLoss: "⚠️ You have moved outside the assessment window. Please stay on the assessment at all times."
    };

    const alertMessage = professionalMessages[type] || message;

    if (this.onCheatDetected) {
      this.onCheatDetected(type, this.cheatCounts[type], alertMessage);
    }

    return true;
  }

  // Record detection violation from EnhancedDetections component
  // Returns true when the violation was actually recorded (and onCheatDetected fired)
  recordDetectionViolation(type, message) {
    // If cheat detection is disabled, don't record any detection violations
    if (this.isCheatDetectionDisabled) {
      console.log(`🔧 CHEAT DETECTION DISABLED: Detection violation ignored - ${type}: ${message}`);
      return false;
    }

    // Check if the specific feature is enabled for this violation type
    let featureKey = null;
    if (type === 'webcamOff' || type === 'noPerson' || type === 'multiplePersons' || type === 'suspiciousObjects') {
      featureKey = 'enableWebcamDetection';
    } else if (type === 'contextMenu') {
      featureKey = 'enableContextMenuDetection';
    } else if (type === 'copyPaste') {
      featureKey = 'enableCopyPasteDetection';
    } else if (type === 'tabSwitch') {
      featureKey = 'enableTabSwitchDetection';
    } else if (type === 'fullscreen') {
      featureKey = 'enableFullscreenDetection';
    } else if (type === 'altKey' || type === 'ctrlKey') {
      featureKey = 'enableKeyboardDetection';
    }
    
    // If feature key exists and feature is disabled, don't record violation
    if (featureKey && !this.isFeatureEnabled(featureKey)) {
      console.log(`🔧 Feature ${featureKey} disabled: Detection violation ignored - ${type}: ${message}`);
      return false;
    }

    if (!this.isExamActive || this.isSubmissionInProgress) return false;

    if (this.cheatCounts.hasOwnProperty(type)) {
      const currentTime = Date.now();
      const lastTime = this.lastViolationTime[type] || 0;

      // Check if enough time has passed since last violation of this type
      if (currentTime - lastTime < this.violationCooldown) {
        return false; // Skip if within cooldown period
      }
      
      // Update last violation time
      this.lastViolationTime[type] = currentTime;
      
      this.cheatCounts[type]++;
      this.saveCheatCounts();

      // Professional alert messages for detection violations
      const professionalMessages = {
        copyPaste: "⚠️ Copy-paste detected. Please type your responses manually.",
        tabSwitch: "⚠️ Tab switching detected. Please remain on the exam page.",
        fullscreen: "⚠️ Fullscreen exit detected. Please return to fullscreen mode.",
        altKey: "⚠️ Alt/Option key detected. Please avoid function keys.",
        ctrlKey: "⚠️ Control/Command key detected. Please avoid keyboard shortcuts.",
        webcamOff: "⚠️ Camera access issue. Please check your webcam connection.",
        contextMenu: "⚠️ Right-click detected. Context menu is disabled during exams.",
        noPerson: "⚠️ No person detected. Please ensure you are visible in camera.",
        multiplePersons: "⚠️ Multiple persons detected. Please ensure only you are visible.",
        suspiciousObjects: "⚠️ Suspicious object detected. Please clear your workspace."
      };

      const alertMessage = professionalMessages[type] || message;

      if (this.onCheatDetected) {
        this.onCheatDetected(type, this.cheatCounts[type], alertMessage);
      }

      return true;
    }

    return false;
  }

  // Get total cheat count
  getTotalCheatCount() {
    // If cheat detection is disabled, return 0
    if (this.isCheatDetectionDisabled) {
      return 0;
    }
    
    return Object.values(this.cheatCounts).reduce((sum, count) => sum + count, 0);
  }

  // Get cheat counts
  getCheatCounts() {
    // If cheat detection is disabled, return all zeros
    if (this.isCheatDetectionDisabled) {
      return {
        copyPaste: 0,
        tabSwitch: 0,
        fullscreen: 0,
        altKey: 0,
        ctrlKey: 0,
        webcamOff: 0,
        contextMenu: 0,
        noPerson: 0,
        multiplePersons: 0,
        suspiciousObjects: 0,
        sidePanel: 0,
        focusLoss: 0
      };
    }
    
    return { ...this.cheatCounts };
  }

  // Request fullscreen
  requestFullscreen() {
    // If cheat detection is disabled, skip fullscreen request
    if (this.isCheatDetectionDisabled) {
      console.log('🔧 CHEAT DETECTION DISABLED: Fullscreen request skipped');
      return Promise.resolve();
    }
    
    const elem = document.documentElement;
    if (elem.requestFullscreen) {
      return elem.requestFullscreen();
    } else if (elem.webkitRequestFullscreen) {
      return elem.webkitRequestFullscreen();
    } else if (elem.msRequestFullscreen) {
      return elem.msRequestFullscreen();
    }
    return Promise.reject(new Error('Fullscreen not supported'));
  }

  // Cleanup all event listeners
  cleanup() {
    // If cheat detection is disabled, no cleanup needed
    if (this.isCheatDetectionDisabled) {
      return;
    }
    
    if (this.webcamCheckInterval) {
      clearInterval(this.webcamCheckInterval);
    }

    if (this.fileUploadTimeout) {
      clearTimeout(this.fileUploadTimeout);
    }

    // Side panel / focus loss detection run on window listeners and their own timers
    clearInterval(this.viewportCheckInterval);
    clearTimeout(this.viewportCheckTimeout);
    clearInterval(this.focusCheckInterval);
    clearTimeout(this.focusCheckTimeout);

    if (this.handlers) {
      if (this.handlers.viewportResize) {
        window.removeEventListener('resize', this.handlers.viewportResize);
        if (window.visualViewport) {
          window.visualViewport.removeEventListener('resize', this.handlers.viewportResize);
        }
      }
      if (this.handlers.windowBlur) {
        window.removeEventListener('blur', this.handlers.windowBlur);
      }
      if (this.handlers.windowFocus) {
        window.removeEventListener('focus', this.handlers.windowFocus);
      }
    }

    if (this.handlers) {
      Object.values(this.handlers).forEach(handler => {
        if (typeof handler === 'function') {
          document.removeEventListener('keydown', handler);
          document.removeEventListener('visibilitychange', handler);
          document.removeEventListener('fullscreenchange', handler);
          document.removeEventListener('contextmenu', handler);
          document.removeEventListener('dragstart', handler);
          document.removeEventListener('drop', handler);
        }
      });
      
      // Clean up observer if it exists
      if (this.handlers.observer) {
        this.handlers.observer.disconnect();
      }
    }
    
    this.isExamActive = false;
  }
}

export default ExamSecurity; 